// The editor's keyboard (live's Shortcuts, #267): one table for the shortcuts modal and the key
// handler, so a row the modal shows as working is a key the editor answers. Pure: no DOM.

/** What a key press asks the editor to do. */
export type Command =
  | "undo"
  | "redo"
  | "copy"
  | "cut"
  | "paste"
  | "remove"
  | "duplicate"
  | "split"
  | "separate"
  | "mute"
  | "lock"
  | "hide"
  | "selectAll"
  | "zoomIn"
  | "zoomOut"
  | "previousFrame"
  | "nextFrame"
  // The rows live greys out, answered here (spec capcut-shortcuts.md).
  | "previewAxis"
  | "snapping"
  | "moveTool"
  | "handTool"
  | "zoomToFit"
  | "zoom50"
  | "zoom100"
  | "zoom200"
  | "canvasZoomIn"
  | "canvasZoomOut"
  | "fullScreen"
  // The main track magnet's edits (spec magnet-ripple): ⇧⌫, Q and W.
  | "rippleRemove"
  | "trimStart"
  | "trimEnd"
  // M (D6): a beat on a chosen sound (spec beats.md), else a marker at the playhead (spec markers.md).
  | "mark";

export interface KeyPress {
  key: string;
  /** The key's place (`KeyboardEvent.code`), which a shifted digit, plus or minus is read by. */
  code?: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

/** Whether this machine names its command key ⌘ (a Mac) rather than Ctrl. */
export function onMac(platform: string): boolean {
  return /mac|iphone|ipad/i.test(platform);
}

/**
 * Letters alone: the preview axis, snapping (live's "Attach"), the canvas's two tools, the trims
 * to the playhead (spec magnet-ripple), and M's beat or marker (specs beats.md and markers.md).
 */
const LETTERS: Readonly<Record<string, Command>> = {
  s: "previewAxis",
  n: "snapping",
  v: "moveTool",
  h: "handTool",
  q: "trimStart",
  w: "trimEnd",
  m: "mark",
};

/** With ⇧ alone: the canvas's zoom, as live's Canvas column writes it, and the ripple delete. */
const SHIFTED: Readonly<Record<string, Command>> = {
  f: "zoomToFit",
  "0": "zoom50",
  "1": "zoom100",
  "2": "zoom200",
  "+": "canvasZoomIn",
  "-": "canvasZoomOut",
  Backspace: "rippleRemove",
  Delete: "rippleRemove",
};

/** The places whose shifted sign depends on the layout (⇧1 types "!" on a US keyboard). */
const PLACES: Readonly<Record<string, string>> = {
  Digit0: "0",
  Digit1: "1",
  Digit2: "2",
  Numpad0: "0",
  Numpad1: "1",
  Numpad2: "2",
  Equal: "+",
  NumpadAdd: "+",
  Minus: "-",
  NumpadSubtract: "-",
};

/** The key a shifted press names on the modal's caps: by its place where it has one, else itself. */
function shiftedKey(press: KeyPress): string {
  const placed = press.code === undefined ? undefined : PLACES[press.code];
  if (placed !== undefined) return placed;
  return press.key.length === 1 ? press.key.toLowerCase() : press.key;
}

/**
 * The command a key press asks for, or null. ⌘ or Ctrl is the "mod" key on every system, as live
 * binds both (`command+z, ctrl+z`); the modal writes the one of the system. A press with Alt asks
 * for nothing here (Alt with an arrow is the timeline's nudge).
 */
export function commandOf(press: KeyPress): Command | null {
  if (press.altKey) return null;
  const mod = press.metaKey || press.ctrlKey;
  const key = press.key.length === 1 ? press.key.toLowerCase() : press.key;
  if (!mod) {
    if (press.shiftKey) return SHIFTED[shiftedKey(press)] ?? null;
    if (key === "Delete" || key === "Backspace") return "remove";
    return LETTERS[key] ?? null;
  }
  if (press.shiftKey) {
    if (key === "z") return "redo";
    if (key === "s") return "separate";
    if (key === "m") return "mute";
    if (key === "f") return "fullScreen";
    // ⌘+ is ⇧⌘= on most layouts.
    if (key === "+") return "zoomIn";
    return null;
  }
  const plain: Record<string, Command> = {
    z: "undo",
    y: "redo",
    c: "copy",
    x: "cut",
    v: "paste",
    d: "duplicate",
    b: "split",
    l: "lock",
    h: "hide",
    a: "selectAll",
    "=": "zoomIn",
    "+": "zoomIn",
    "-": "zoomOut",
    ArrowLeft: "previousFrame",
    ArrowRight: "nextFrame",
  };
  return plain[key] ?? null;
}

/** A key as the modal and the menus write it: "mod" is ⌘ on a Mac and Ctrl elsewhere. */
export type KeyCap = "mod" | "shift" | (string & {});

export function capText(cap: KeyCap, mac: boolean): string {
  if (cap === "mod") return mac ? "⌘" : "Ctrl";
  if (cap === "shift") return "⇧";
  return cap;
}

export interface ShortcutRow {
  label: string;
  keys: readonly KeyCap[];
  /** False for a row the editor does not answer: live greys its own; ours are written in E1. */
  works: boolean;
}

export interface ShortcutGroup {
  title: "Globe" | "Timeline" | "Canvas";
  rows: readonly ShortcutRow[];
}

/**
 * Live's shortcuts modal, row for row (`editor-depth/02`). Live greys 16 rows it does not wire;
 * this editor answers 13 of them (spec capcut-shortcuts.md) and M (specs beats.md and markers.md), and greys the
 * transcript's two, which have no editor here. The canvas's moves move the chosen pictures' and
 * texts' places, with the canvas focused. Split is ⌘B in the modal and in the menu (the lead's
 * decision on #267; live's menu says ⌘K).
 */
export const SHORTCUTS: readonly ShortcutGroup[] = [
  {
    title: "Globe",
    rows: [
      { label: "Select all", keys: ["mod", "A"], works: true },
      { label: "Select multiple clips", keys: ["shift", "Left-Click"], works: true },
      { label: "Copy", keys: ["mod", "C"], works: true },
      { label: "Cut", keys: ["mod", "X"], works: true },
      { label: "Paste", keys: ["mod", "V"], works: true },
      { label: "Delete", keys: ["⌫"], works: true },
      { label: "Undo", keys: ["mod", "Z"], works: true },
      { label: "Redo", keys: ["shift", "mod", "Z"], works: true },
      { label: "Play or pause", keys: ["Space"], works: true },
      { label: "Text wrap", keys: ["mod", "Enter"], works: false },
      { label: "Split sentence", keys: ["Enter"], works: false },
    ],
  },
  {
    title: "Timeline",
    rows: [
      { label: "Split", keys: ["mod", "B"], works: true },
      { label: "Zoom in", keys: ["mod", "+"], works: true },
      { label: "Zoom out", keys: ["mod", "-"], works: true },
      { label: "Scroll up or down", keys: ["Scroll"], works: true },
      { label: "Scroll left or right", keys: ["shift", "Scroll"], works: true },
      { label: "Last frame", keys: ["mod", "←"], works: true },
      { label: "Next frame", keys: ["mod", "→"], works: true },
      { label: "Turn on or off preview axis", keys: ["S"], works: true },
      { label: "Attach", keys: ["N"], works: true },
      { label: "Separate or restore audio", keys: ["shift", "mod", "S"], works: true },
      // Beyond live (spec magnet-ripple): CapCut's ripple delete and trims to the playhead.
      { label: "Ripple delete", keys: ["shift", "⌫"], works: true },
      { label: "Trim start to playhead", keys: ["Q"], works: true },
      { label: "Trim end to playhead", keys: ["W"], works: true },
      { label: "Add or remove beats", keys: ["M"], works: true },
    ],
  },
  {
    title: "Canvas",
    rows: [
      { label: "Full screen", keys: ["shift", "mod", "F"], works: true },
      { label: "Move", keys: ["V"], works: true },
      { label: "Hand tool", keys: ["H"], works: true },
      { label: "Zoom in", keys: ["shift", "+"], works: true },
      { label: "Zoom out", keys: ["shift", "-"], works: true },
      { label: "Zoom to fit", keys: ["shift", "F"], works: true },
      { label: "Zoom to 50%", keys: ["shift", "0"], works: true },
      { label: "Zoom to 100%", keys: ["shift", "1"], works: true },
      { label: "Zoom to 200%", keys: ["shift", "2"], works: true },
      { label: "Move up 1 px", keys: ["↑"], works: true },
      { label: "Move down 1 px", keys: ["↓"], works: true },
      { label: "Move left 1 px", keys: ["←"], works: true },
      { label: "Move right 1 px", keys: ["→"], works: true },
      { label: "Move 5 px", keys: ["shift", "Arrow Keys"], works: true },
    ],
  },
];

/** The keys of the timeline menu's items, as live's menu shows them (Split is ⌘B, as above). */
export const MENU_KEYS: Readonly<Record<string, readonly KeyCap[]>> = {
  Copy: ["mod", "C"],
  Paste: ["mod", "V"],
  "Paste at Playhead": ["mod", "V"],
  Duplicate: ["mod", "D"],
  "Split at Playhead": ["mod", "B"],
  "Separate Audio": ["shift", "mod", "S"],
  "Remove Background": [],
  "Restore Original": [],
  Mute: ["mod", "shift", "M"],
  Unmute: ["mod", "shift", "M"],
  Lock: ["mod", "L"],
  Unlock: ["mod", "L"],
  Hide: ["mod", "H"],
  Show: ["mod", "H"],
  Flip: [],
  "Flip Horizontal": [],
  "Flip Vertical": [],
  "Rotate 90°": [],
  Delete: ["⌫"],
};
